import React from 'react';
import * as yup from "yup";
import {DateFormatter} from "../../../../services/date";

import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import {BookingPrice} from "../../BookingComponents";

import FormPayment from "./FormPayment";
import {OptionalContent} from "../../../../components/OptionalContent";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import PaymentMenu from "./PaymentMenu";
import {useBookingFinanceUpdate, usePaymentDelete} from "../../query";
import {useFeedbackTools} from "../../../../components/FeedbackTools";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import TextAttributeForm from "../../../../components/AttributeForm/TextAttributeForm";
import useDialog from "../../../../hooks/useDialog";
import BookingFinanceDocument from "./BookingFinanceDocument";
import {nonNegativeNumber} from "../../../../services/validationSchema";
import Divider from "@mui/material/Divider";
import FormExtraCharge from "./FormExtraCharge";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import Alert from "@mui/material/Alert";
import {TOURIST_TAX_POLICIES} from "../../../../services/booking_conditions/enums";


const lastCellWidth = 40;


const formatPrice = (num, maxDigits = 2) => {
    // Use toFixed to round, parseFloat to remove trailing zeros
    return parseFloat(num.toFixed(maxDigits));
};


const PaymentTab = ({booking, accommodations}) => {
    const bookingFinance = booking.bookingFinance;
    const paymentDeleteMutation = usePaymentDelete({bookingId: booking.id});
    const bookingFinanceUpdateMutation = useBookingFinanceUpdate({bookingId: booking.id});

    const {showErrorNotification, askConfirmation} = useFeedbackTools();

    const [formPaymentInitialValues, setFormPaymentInitialValues] = React.useState(null);

    const discountDialog = useDialog();
    const touristTaxDialog = useDialog();
    const securityDepositDialog = useDialog();
    const extraChargeDialog = useDialog();

    const openFormPayment = (transactionType, initialAmount = null, initialReasonOfPayment = null) => {
        setFormPaymentInitialValues({transactionType, initialAmount, initialReasonOfPayment});
    }

    const closeFormPayment = () => {
        setFormPaymentInitialValues(null);
    }

    const handleDeletePayment = (paymentId) => {
        const innerDelete = async () => {
            try {
                await paymentDeleteMutation.mutateAsync({paymentId});
            } catch (e) {
                showErrorNotification(e);
            }
        }
        askConfirmation({
            title: "Elimina pagamento",
            description: `Confermando sarà eliminato il pagamento. Confermi l'eliminazione del pagamento selezionato?`,
            confirmationText: 'Si',
            cancellationText: 'No',
            onConfirm: innerDelete
        });
    }

    const handleDeleteExtraCharge = (extraChargeId) => {
        const innerDelete = async () => {
            try {
                await bookingFinanceUpdateMutation.mutateAsync({data: {extraChargesAttributes: [{id: extraChargeId, _destroy: true}]}});
            } catch (e) {
                showErrorNotification(e);
            }
        }
        askConfirmation({
            title: "Elimina extra",
            description: `Confermando sarà eliminato l'extra. Confermi l'eliminazione dell'extra selezionato?`,
            confirmationText: 'Si',
            cancellationText: 'No',
            onConfirm: innerDelete
        });
    }

    const handleUpdateDiscount = async (values) => {
        try {
            await bookingFinanceUpdateMutation.mutateAsync({data: {discount: values.discount}});
            discountDialog.close();
        } catch (e) {
            showErrorNotification(e);
        }
    }

    const handleUpdateTouristTax = async (values) => {
        try {
            await bookingFinanceUpdateMutation.mutateAsync({data: {hostProvidedTouristTax: values.hostProvidedTouristTax}});
            touristTaxDialog.close();
        } catch (e) {
            showErrorNotification(e);
        }
    }

    const handleUpdateSecurityDeposit = async (values) => {
        try {
            await bookingFinanceUpdateMutation.mutateAsync({data: {securityDeposit: values.securityDeposit}});
            securityDepositDialog.close();
        } catch (e) {
            showErrorNotification(e);
        }
    };

    const payments = bookingFinance.payments;
    const isTouristTaxIncluded = booking.bookingCondition?.touristTaxPolicy === TOURIST_TAX_POLICIES.INCLUDED;

    return (
        <>
            <Stack sx={{p: 2}} spacing={4}>

                <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography sx={{flexGrow: 1}} variant='body1' fontWeight='bold'>
                            Costi e Extra
                        </Typography>
                        <Button color="primary"
                                onClick={extraChargeDialog.open}
                                size="small"
                                startIcon={<AddIcon fontSize="inherit"/>}>
                            Aggiungi
                        </Button>
                    </Stack>

                    <TableContainer>
                        <Divider />
                        <Table size="small" aria-label="list of payments">
                            <TableBody>
                                {
                                    booking.bookingItems.map(bi => {
                                        const apartment = accommodations.findApartment(bi.bookableId);

                                        return <TableRow key={bi.id}>
                                            <TableCell align="left">
                                                {`${apartment?.accommodation?.name} - ${apartment?.name}`}
                                            </TableCell>
                                            <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>
                                                {bi.price ? formatPrice(bi.price) : '--'} €
                                            </TableCell>
                                            <TableCell align="right" sx={{ width: lastCellWidth }}/>
                                        </TableRow>
                                    })
                                }
                                {
                                    bookingFinance.extraCharges.map(ec => (
                                        <TableRow key={ec.id}>
                                            <TableCell align="left">{ec.descriptionOther}</TableCell>
                                            <TableCell align="right">{ec.price ? formatPrice(ec.price) : '--'} €</TableCell>
                                            <TableCell align="right" sx={{ width: lastCellWidth }}>
                                                <IconButton size="small"
                                                            color="error"
                                                            sx={{p: 0}}
                                                            onClick={() => handleDeleteExtraCharge(ec.id)}>
                                                    <DeleteIcon fontSize="inherit"/>
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography sx={{flexGrow: 1}} variant='body1' fontWeight='bold'>
                            Pagamenti
                        </Typography>
                        <PaymentMenu booking={booking} openFormPayment={openFormPayment}/>
                    </Stack>

                    <OptionalContent hasContent={payments.length > 0}
                                     noContentMessage="Nessun pagamento registrato">
                        <TableContainer>
                            <Divider />
                            <Table size="small" aria-label="list of payments">
                                <TableBody>
                                    {
                                        payments.map(p => (
                                            <TableRow key={p.id}>
                                                <TableCell align="left">
                                                    {DateFormatter.dmyString(p.date)} - {p.getReasonLabel()}
                                                    <Typography variant="body2" color="textSecondary">
                                                        {p.getTypeLabel()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>{formatPrice(p.amount)} €</TableCell>
                                                <TableCell align="right" sx={{ width: lastCellWidth }}>
                                                    <IconButton size="small"
                                                                color="error"
                                                                sx={{p: 0}}
                                                                onClick={() => handleDeletePayment(p.id)}>
                                                        <DeleteIcon fontSize="inherit"/>
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </OptionalContent>
                </Box>

                <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography sx={{flexGrow: 1}} variant='body1' fontWeight='bold'>
                            Altro
                        </Typography>
                    </Stack>
                    <TableContainer>
                        <Divider />
                        <Table size="small" aria-label="list of payments">
                            <TableBody>
                                <TableRow>
                                    <TableCell align="left">
                                        Tassa di soggiorno
                                        {
                                            !bookingFinance.isTouristTaxProvidedByHost && bookingFinance.touristTaxRecords.map(ttr => (
                                                <Typography key={ttr.id} variant="body2" color="textSecondary">
                                                    {ttr.excludedGuestsCount} esenti, {ttr.taxableGuestsCount} ospiti x {ttr.taxableNights} notti x {ttr.unitPrice}€
                                                </Typography>
                                            ))
                                        }
                                        {
                                            isTouristTaxIncluded && <Typography variant="body2" color="textSecondary">
                                                Inclusa nel costo della prenotazione.
                                            </Typography>
                                        }
                                    </TableCell>
                                    <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>
                                        {bookingFinance.touristTax ?? '--'} €
                                    </TableCell>
                                    <TableCell align="right" sx={{ width: lastCellWidth }}>
                                        <IconButton size="small"
                                                    sx={{p: 0}}
                                                    disabled={isTouristTaxIncluded}
                                                    onClick={touristTaxDialog.open}>
                                            <EditIcon fontSize="inherit"/>
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="left">Depositio cauzionale</TableCell>
                                    <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>
                                        {bookingFinance.securityDeposit ? `${bookingFinance.securityDeposit }` : '--'} €
                                    </TableCell>
                                    <TableCell align="right" sx={{ width: lastCellWidth }}>
                                        <IconButton size="small"
                                                    sx={{p: 0}}
                                                    onClick={securityDepositDialog.open}>
                                            <EditIcon fontSize="inherit"/>
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="left">Sconto</TableCell>
                                    <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>
                                        {bookingFinance.discount ? `- ${bookingFinance.discount }` : '--'} €
                                    </TableCell>
                                    <TableCell align="right" sx={{ width: lastCellWidth }}>
                                        <IconButton size="small"
                                                    sx={{p: 0}}
                                                    onClick={discountDialog.open}>
                                            <EditIcon fontSize="inherit"/>
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {
                        !bookingFinance.isTouristTaxProvidedByHost && bookingFinance.touristTax && <Alert severity="info" sx={{mt: 2}}>
                            La <b>tassa di soggiorno</b> è stata calcolata utilizzando le informazioni della prenotazione, degli
                            ospiti e dei check-in e check-out se presenti. Assicurarsi che l'importo sia quello stabilito
                            dagli enti locali e modificarlo se necessario.
                        </Alert>
                    }
                    {
                        bookingFinance.securityDeposit && <Alert severity="info" sx={{mt: 2}}>
                            Il <b>deposito cauzionale</b> non è incluso nel totale della prenotazione e viene gestito separatamente.
                        </Alert>
                    }
                </Box>

                <Box>
                    <Typography sx={{flexGrow: 1}} variant='body1' fontWeight='bold'>
                        Riepilogo
                    </Typography>
                    <BookingPrice price={bookingFinance.totalPrice}
                                  payed={bookingFinance.totalPayed}
                                  balance={bookingFinance.balance}/>
                </Box>

                <BookingFinanceDocument booking={booking}/>
            </Stack>
            {
                formPaymentInitialValues && <FormPayment booking={booking}
                                                         onClose={closeFormPayment}
                                                         {...formPaymentInitialValues}
                />
            }
            {
                extraChargeDialog.show && <FormExtraCharge bookingFinance={bookingFinance}
                                                           onClose={extraChargeDialog.close} />
            }
            {
                discountDialog.show && <TextAttributeForm label="Sconto"
                                                          attribute="discount"
                                                          value={bookingFinance.discount}
                                                          dialogTitle="Aggiorna sconto"
                                                          onClose={discountDialog.close}
                                                          onSubmit={handleUpdateDiscount}
                                                          InputProps={{
                                                              endAdornment: <Typography sx={{ml: 1}}>€</Typography>
                                                          }}
                                                          validationSchema={yup.object().shape({discount: nonNegativeNumber})}
                                                          isLoading={bookingFinanceUpdateMutation.isLoading}/>
            }
            {
                touristTaxDialog.show && <TextAttributeForm label="Tassa di soggiorno"
                                                            attribute="hostProvidedTouristTax"
                                                            value={bookingFinance.touristTax}
                                                            dialogTitle="Aggiorna tassa di soggiorno"
                                                            onClose={touristTaxDialog.close}
                                                            onSubmit={handleUpdateTouristTax}
                                                            InputProps={{
                                                                endAdornment: <Typography sx={{ml: 1}}>€</Typography>
                                                            }}
                                                            validationSchema={yup.object().shape({hostProvidedTouristTax: nonNegativeNumber})}
                                                            isLoading={bookingFinanceUpdateMutation.isLoading}/>
            }
            {
                securityDepositDialog.show && <TextAttributeForm label="Deposito cauzionale"
                                                                 attribute="securityDeposit"
                                                                 value={bookingFinance.securityDeposit}
                                                                 dialogTitle="Aggiorna deposito cauzionale"
                                                                 onClose={securityDepositDialog.close}
                                                                 onSubmit={handleUpdateSecurityDeposit}
                                                                 InputProps={{
                                                                     endAdornment: <Typography
                                                                         sx={{ml: 1}}>€</Typography>
                                                                 }}
                                                                 validationSchema={yup.object().shape({securityDeposit: nonNegativeNumber})}
                                                                 isLoading={bookingFinanceUpdateMutation.isLoading}/>
            }
        </>
    );
};

export default PaymentTab;